// Recently-played games, kept in this browser's localStorage. This is the
// installed app's way back into games first opened elsewhere: iOS never
// routes tapped links into an installed web app, so the app remembers every
// game it has seen and offers them on the home screen.

export interface RecentGame {
  key: string;
  title: string | null;
  status: string | null;
  lastSeen: number;
}

const STORAGE_KEY = 'recentGames';
const MAX_ENTRIES = 10;

export function getRecentGames(): RecentGame[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is RecentGame => typeof entry?.key === 'string' && typeof entry?.lastSeen === 'number'
    );
  } catch {
    return [];
  }
}

export function rememberGame(key: string, title: string | null, status: string | null): void {
  try {
    const rest = getRecentGames().filter((entry) => entry.key !== key);
    const next: RecentGame[] = [{ key, title, status, lastSeen: Date.now() }, ...rest].slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full or unavailable — the list is a convenience, never fatal
  }
}

// Accepts a full game link (any origin), a pathname, or a bare game key and
// returns the game key, or null if the input doesn't look like one.
export function parseGameKey(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const urlMatch = trimmed.match(/games\/([A-Za-z0-9-]+)/);
  if (urlMatch) return urlMatch[1];

  if (/^[A-Za-z0-9-]{8,}$/.test(trimmed)) return trimmed;

  return null;
}
